import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    VerticalBarLineBarMouseEvent,
    VerticalBarLineComponent,
} from '@cmps/ng-chart';
import { VerticalBarLineRankAxisMouseEvent } from '@cmps/ng-chart/vertical-bar-line.component';
import { TranslateService } from '@ngx-translate/core';

import { CmpsDatePipe } from '../../../common/date.pipe';
import { StateToProps } from '../../../store';
import { useDispatcher, UseStore } from '../../../store.decorator';
import { RootDefinition } from '../../core/definition.scorecard.model';
import { Scorecard } from '../../scorecard-next.model';
import { TranslationKeyMap } from '../../scorecard.i18n';
import {
    PeriodType,
    PeriodTypeShallow,
    ScorecardDto,
} from '../../scorecard.model';

import {
    filterRanks,
    filterScorecardPublishStatus,
    filterSuperGroups,
    navigate,
    scorecardTrendSelectionChange,
    selectView,
    setTrendChartData,
    setTrendChartFilters,
} from './scorecard-trend-chart.action';
import {
    RankFilter,
    ScorecardPublishStatusFilter,
    StcVerticalBarLineDataBar,
    StcVerticalBarLineDataBarGroup,
    StcVerticalBarLineOptions,
    SuperGroupFilter,
    TooltipData,
} from './scorecard-trend-chart.model';
import scorecardTrendChartReducer from './scorecard-trend-chart.reducer';
import {
    NUM_BARS_COMPACT,
    NUM_BARS_WIDE,
    ScorecardTrendChartState,
    View,
} from './scorecard-trend-chart.state';

@Component({
    selector: 'cmps-scorecard-trend-chart-widget',
    templateUrl: './scorecard-trend-chart-widget.component.html',
})
@UseStore<ScorecardTrendChartState>(
    'scorecard.widgets.trendChart',
    scorecardTrendChartReducer.trendChart,
)
export class ScScorecardTrendChartWidgetComponent
    implements
        OnInit,
        OnChanges,
        OnDestroy,
        StateToProps<ScorecardTrendChartState>
{
    state: ScorecardTrendChartState;

    @ViewChild(VerticalBarLineComponent)
    private readonly _chart: VerticalBarLineComponent;

    @Input() periodTypes: PeriodTypeShallow[];
    @Input() scorecardHistoryData: ScorecardDto[];
    @Input() scorecardTrendChartRootDefinition: RootDefinition;
    @Input() selectedPeriodType: PeriodType;
    @Input() selectedScorecard: Scorecard;
    @Input() fromInsightCard: boolean;
    @Input() selectedTabId: string;

    @Output() onPeriodTypeChange: EventEmitter<string>;
    @Output() onPeriodTypeFocus: EventEmitter<void>;
    @Output() onVerticalBarSelectionChange: EventEmitter<string>;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _View = View;
    /** @private */ _verticalBarLineOptions: StcVerticalBarLineOptions;

    /** @private */ get _hasDataToShow(): boolean {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state &&
            this.state.selectedTrendChart &&
            this.state.selectedTrendChart.publishStatusFilter &&
            this.state.selectedTrendChart.publishStatusFilter.length > 0 &&
            this.state.selectedTrendChart.supergroupsFilter &&
            this.state.selectedTrendChart.supergroupsFilter.length > 0
        );
    }

    /** @private */ get _hasFilteredDataToShow(): boolean {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this.state &&
            this.state.selectedTrendChart &&
            this.state.selectedTrendChart.scorecardTrendChartData &&
            this.state.selectedTrendChart.scorecardTrendChartData
                .verticalBarLineData &&
            this.state.selectedTrendChart.scorecardTrendChartData
                .verticalBarLineData.bars &&
            this.state.selectedTrendChart.scorecardTrendChartData
                .verticalBarLineData.bars.length > 0
        );
    }

    /** @private */ _collapsed: boolean;
    /** @private */ _isLoadingData: boolean;
    /** @private */ _tooltipData: TooltipData;
    /** @private */ _rankTooltipData: string;

    private readonly _defaultTooltipGap: number;

    private get _selectedVerticalBarScorecardId(): string {
        const bars =
            this.state.selectedTrendChart.scorecardTrendChartData
                .verticalBarLineData.bars;
        const selectedBarGroup = bars.find((g) =>
            g.values.some((b) => b.isSelected),
        );
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const selectedBar =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            selectedBarGroup &&
            selectedBarGroup.values.find((b) => b.isSelected);

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return (selectedBar && selectedBar.value.scorecardId.toString()) || '0';
    }

    private get _rankTooltipElement(): HTMLElement {
        return document.getElementById('sc-rank-tooltip');
    }

    private get _tooltipElement(): HTMLElement {
        return document.getElementById('sc-tooltip');
    }

    constructor(
        private readonly _translateService: TranslateService,
        private readonly _datePipe: CmpsDatePipe,
    ) {
        this._collapsed = true;
        this._isLoadingData = false;
        this._defaultTooltipGap = 25;
        this.onPeriodTypeChange = new EventEmitter();
        this.onPeriodTypeFocus = new EventEmitter();
        this.onVerticalBarSelectionChange = new EventEmitter();
        this._tooltipData = {};
        this._rankTooltipData = '';
    }

    ngOnInit(): void {
        this._reload(true);
        this._verticalBarLineOptions = this._getVerticalBarLineOptions();
        if (
            this.scorecardTrendChartRootDefinition.scorecardTrendChart
                .collapsed === false ||
            this.fromInsightCard === true
        ) {
            this._collapsed = false;
        }
    }

    ngOnDestroy(): void {
        this.onPeriodTypeChange.complete();
        this.onPeriodTypeFocus.complete();
        this.onVerticalBarSelectionChange.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.state) {
            return;
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('scorecardHistoryData')) {
            // eslint-disable-next-line no-prototype-builtins
            this._reload(
                // eslint-disable-next-line no-prototype-builtins
                changes.hasOwnProperty('scorecardTrendChartRootDefinition'),
            );
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    stateToProps(prevState?: ScorecardTrendChartState) {
        if (prevState.selectedView !== this.state.selectedView) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._chart && this._chart.render(0).catch(() => null);
        }

        if (prevState.navigation !== this.state.navigation) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._chart &&
                this._chart
                    .navigate(this.state.navigation.numPages)
                    .catch(() => null);
        }
    }

    /** @private */ _onToggleExpandClick(): void {
        this._collapsed = !this._collapsed;
    }

    /** @private */ _onTrendViewClick(
        $event: MouseEvent | KeyboardEvent,
        view: View,
    ): void {
        $event.preventDefault();
        $event.stopImmediatePropagation();
        useDispatcher(this, selectView({ view }));
    }

    /** @private */ _onPeriodTypeFocus(): void {
        this.onPeriodTypeFocus.emit();
    }

    /** @private */ _onPeriodTypeChange(periodType: string): void {
        this.onPeriodTypeChange.emit(periodType);
    }

    /** @private */ _onRendered(): void {
        this._chart.scrollSelectedIntoView(0).catch(() => null);
    }

    /** @private */ _onNavigateClick(
        $event: MouseEvent | KeyboardEvent,
        numPages: number,
    ): void {
        $event.preventDefault();
        $event.stopImmediatePropagation();
        useDispatcher(this, navigate({ numPages }));
    }

    /** @private */ _onRankAxisMouseOver(
        data: VerticalBarLineRankAxisMouseEvent,
    ): void {
        if (data.d) {
            this._rankTooltipData = data.d;
            this._rankTooltipElement.style.display = 'block';

            this._positionTooltip(data.event, this._rankTooltipElement, 'r');
        }
    }

    /** @private */ _onRankAxisMouseOut(): void {
        this._rankTooltipElement.style.display = 'none';
    }

    /** @private */ _onRankFilterChange(rankFilter: RankFilter[]): void {
        useDispatcher(
            this,
            filterRanks({
                ranks: rankFilter,
                scorecardHistory: this.scorecardHistoryData,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                selectedScorecardId:
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    (this.selectedScorecard &&
                        this.selectedScorecard.header &&
                        this.selectedScorecard.header.scorecardId) ||
                    0,
            }),
        );
    }

    /** @private */ _onScorecardPublishStatusFilterChange(
        scorecardPublishedStatusFilter: ScorecardPublishStatusFilter[],
    ): void {
        useDispatcher(
            this,
            filterScorecardPublishStatus({
                scorecardPublishStatuses: scorecardPublishedStatusFilter,
                scorecardHistory: this.scorecardHistoryData,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                selectedScorecardId:
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    (this.selectedScorecard &&
                        this.selectedScorecard.header &&
                        this.selectedScorecard.header.scorecardId) ||
                    0,
            }),
        );
        this.onVerticalBarSelectionChange.emit(
            this._selectedVerticalBarScorecardId,
        );
    }

    /** @private */ _onSuperGroupsFilterChange(
        superGroupsFilter: SuperGroupFilter[],
    ): void {
        useDispatcher(
            this,
            filterSuperGroups({
                superGroups: superGroupsFilter,
                scorecardHistory: this.scorecardHistoryData,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                selectedScorecardId:
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    (this.selectedScorecard &&
                        this.selectedScorecard.header &&
                        this.selectedScorecard.header.scorecardId) ||
                    0,
            }),
        );
        this.onVerticalBarSelectionChange.emit(
            this._selectedVerticalBarScorecardId,
        );
    }

    /** @private */ _onVerticalBarHover(
        data: VerticalBarLineBarMouseEvent,
    ): void {
        const d = data.d as StcVerticalBarLineDataBar;
        const barData =
            d &&
            this.scorecardHistoryData &&
            this.scorecardHistoryData.find(
                (x) => x.scorecardId === d.value.scorecardId,
            );
        if (barData) {
            this._tooltipData = {
                startDate: barData.startDate,
                endDate: barData.endDate,
                superGroup: barData.groupShortName || barData.groupName,
                score: d.value.y,
                ranks: barData.ranks,
                published: this.scorecardHistoryData.some((x) => !x.published)
                    ? barData.published === true
                    : null,
            };
            this._tooltipElement.style.display = 'block';

            this._positionTooltip(data.event, this._tooltipElement);
        }
    }

    /** @private */ _onVerticalBarOut(): void {
        this._tooltipElement.style.display = 'none';
    }

    /** @private */ _onVerticalBarSelected(value: any): void {
        useDispatcher(
            this,
            scorecardTrendSelectionChange({ selectedScorecard: value }),
        );
        this.onVerticalBarSelectionChange.emit(value.value.scorecardId);
    }

    /** @private */ _positionTooltip(
        e: MouseEvent,
        tooltip: HTMLElement,
        type: 'b' | 'r' = 'b',
    ): void {
        const mousePosX = e.pageX;
        const mousePosY = e.pageY;
        const tooltipWidth = tooltip.offsetWidth;
        const tooltipHalfHeight = tooltip.offsetHeight / 2;

        const maxPosXTooltip =
            mousePosX + this._defaultTooltipGap + tooltipWidth;

        tooltip.style.top = `${
            mousePosY - tooltipHalfHeight - window.pageYOffset
        }px`;

        if ($(window).width() > maxPosXTooltip) {
            tooltip.style.left = `${mousePosX + this._defaultTooltipGap}px`;
            tooltip.classList.add(
                `sc-tooltip-${type === 'r' ? 'rank-' : ''}left`,
            );
            tooltip.classList.remove(
                `sc-tooltip-${type === 'r' ? 'rank-' : ''}right`,
            );
        } else {
            tooltip.style.left = `${
                mousePosX - (this._defaultTooltipGap + tooltipWidth) + 5
            }px`;
            tooltip.classList.add(
                `sc-tooltip-${type === 'r' ? 'rank-' : ''}right`,
            );
            tooltip.classList.remove(
                `sc-tooltip-${type === 'r' ? 'rank-' : ''}left`,
            );
        }
    }

    private _reload(reset: boolean): void {
        if (!this.scorecardHistoryData) {
            return;
        }
        this._isLoadingData = true;
        useDispatcher(
            this,
            setTrendChartFilters({
                periodType: this.selectedPeriodType,
                tabId: this.selectedTabId,
                scorecardHistory: this.scorecardHistoryData,
                rankColourPool:
                    this.scorecardTrendChartRootDefinition.scorecardTrendChart
                        .rankColourPool,
                superGroupColourPool:
                    this.scorecardTrendChartRootDefinition.scorecardTrendChart
                        .superGroupColourPool,
                reset,
            }),
        );
        useDispatcher(
            this,
            setTrendChartData({
                periodType: this.selectedPeriodType,
                tabId: this.selectedTabId,
                scorecardHistory: this.scorecardHistoryData,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                selectedScorecardId:
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    (this.selectedScorecard &&
                        this.selectedScorecard.header &&
                        this.selectedScorecard.header.scorecardId) ||
                    0,
            }),
        );
        this._isLoadingData = false;
    }

    private _getVerticalBarLineOptions(): StcVerticalBarLineOptions {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {
            rect: {
                width: '100%',
                height: 310,
                top: 0,
                right: 0,
                bottom: 20,
                left: 0,
            },
            barsPerViewPort: () =>
                this.state.selectedView === View.COMPACT
                    ? NUM_BARS_COMPACT
                    : NUM_BARS_WIDE,
            navAnchorPadding: () => 15, // icon width / 2
            barLabel: (d) =>
                !d ? null : this.state.selectedView === 'wide' ? d.name : null,
            barColor: (d) => {
                if (!d) {
                    return null;
                }

                const sgFilter =
                    this.state.selectedTrendChart.supergroupsFilter.find(
                        (x) => x.id === d.groupId,
                    );
                if (!sgFilter) {
                    return null;
                }

                let colour = sgFilter.colour;
                if (!d.isPublished) {
                    colour = `#${colour
                        .replace(/^#/, '')
                        .replace(/../g, (color) =>
                            `0${Math.min(
                                255,
                                Math.max(0, parseInt(color, 16) + 70),
                            ).toString(16)}`.substr(-2),
                        )}`;
                }

                return colour;
            },
            barPatterned: (d) => d && !d.isPublished,
            barBorderWidth: () => 3,
            barBorderColor: () => '#D17300',
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            barSelected: (_, d) => d && d.isSelected,
            lineWidth: () => 2,
            lineColor: (d) => {
                if (!d) return null;

                return this.state.selectedTrendChart.ranksFilter.find(
                    (x) => x.rankName === d.key,
                ).colour;
            },
            xAxisLabel: (g: StcVerticalBarLineDataBarGroup, d: string) => {
                switch (this.selectedPeriodType) {
                    case PeriodType.QTD:
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        return `${this._datePipe.transform(
                            g.startDate,
                            'MMM',
                            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        )} - ${this._datePipe.transform(
                            g.endDate,
                            'MMM yyyy',
                        )}`;
                    case PeriodType.YTD:
                        return `${g.endDate.getFullYear()}`;
                    default:
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        return `${this._datePipe.transform(
                            g.endDate,
                            'MMM - yyyy',
                        )}`;
                }
            },
            yDomain: (d: string, yDomain: [number, number]) => {
                if (d && d === 'score') {
                    yDomain[0] = 0; // Min value for 'score' axis must be always 0
                }

                if (d && d !== 'score') {
                    // Rank values must be sorted from top to bottom
                    yDomain[1] = 1; // Min value for 'ranks' axis must be always 1
                    yDomain[0] = this.state.selectedTrendChart.ranksFilter.find(
                        (x) => x.rankName === d,
                    ).maxRankRange;
                }

                if (yDomain[0] === yDomain[1]) {
                    return [yDomain[0], yDomain[0] + 1];
                }

                return yDomain;
            },
            yAxisLabel: (d: string) => {
                if (d && d === 'score') {
                    return this._translateService.instant(
                        TranslationKeyMap._752_SCORE,
                    );
                }

                if (d && d !== 'score') {
                    return null;
                }

                return d;
            },
        } as StcVerticalBarLineOptions;
    }
}
